import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import LinkButton from 'flarum/common/components/LinkButton';
import TextEditor from 'flarum/common/components/TextEditor';
import Topic from './models/Topic';
import TopicPage from './components/TopicPage';
import TopicsPage from './components/TopicsPage';
import TopicCounter from './components/TopicCounter';
import HotTopics from './components/HotTopics';
import TopicAside from './components/TopicAside';
import registerMentionable from './registerMentionable';
import { topicSettings } from './state/topicSettings';
app.initializers.add('ziiven-topics', () => {
  app.store.models.topics = Topic;
  app.routes.topic = {
    path: '/topic/:name',
    component: TopicPage
  };
  app.routes.topics = {
    path: '/topics',
    component: TopicsPage
  };

  /**
   * The per-post quota and the hot area's switches are not in `app.data` —
   * Flarum 2.0's CorePayload carries no settings — so they come from a small
   * read-only endpoint.
   *
   * beforeMount, not the initializer itself: Flarum assigns `app.forum`
   * after the initializers have run, and the API URL lives on the forum
   * model. Asking for it too early throws, and a throw in an initializer
   * takes the whole front end down with it.
   */
  app.beforeMount(() => {
    topicSettings();
  });

  /**
   * The composer counter: "3 / 5 topic tags used", on the opposite side of
   * the footer from the submit button.
   *
   * A priority above the submit button's (which is 0) puts the counter first
   * in the list, and the footer's space-between puts it on the left.
   */
  extend(TextEditor.prototype, 'controlItems', function (items) {
    items.add('ziiven-topic-counter', m(TopicCounter, null), 10);
  });

  /**
   * Composer autocomplete. Guarded rather than required: topics parse, link
   * and browse perfectly well without flarum/mentions — you just lose the
   * suggestions while typing. The import lives behind this check because
   * `ext:flarum/mentions/…` does not resolve when mentions isn't installed.
   */
  if ('flarum-mentions' in flarum.extensions) {
    registerMentionable();
  }

  /**
   * Discoverability: without an entry point, nobody finds /topics.
   *
   * IndexSidebar, not IndexPage — Flarum 2 moved the nav list out of the page
   * component, and extending IndexPage here adds an item to a list nothing
   * renders. Priority -10 puts it below the stock items rather than above
   * "All Discussions".
   *
   * The theme draws this list with `new IndexSidebar().navItems()`, so this is
   * the list that is actually on screen.
   *
   * Only the browse entry is here: the hot topics have their own place in
   * the right hand column of the discussion page, and a second entry point
   * in the left hand nav would only say the same thing twice.
   *
   * The icon is left unstyled on purpose: the theme gives every icon in this
   * list the same colour, and a colour of our own here would make this one
   * the odd one out — a difference nobody asked for and nobody can explain.
   * (It read as a bug on the production theme, where the theme's own grey
   * and ours were two different greys; on the local theme they happened to
   * be close enough to hide it.)
   */
  extend(IndexSidebar.prototype, 'navItems', function (items) {
    items.add('topics', m(LinkButton, {
      href: app.route('topics'),
      icon: "fas fa-tags"
    }, app.translator.trans('ziiven-topics.forum.nav.browse')), 90);
  });

  /**
   * The hot topics, in the places the design puts them.
   *
   * - the home page gets the card grid across the top, and (on mobile) a bar
   *   along the bottom;
   * - the topic page gets the compact list in its own right hand column.
   *
   * Each place has its own switch, and each component checks it itself, so
   * switching one off does not need the others to know about it.
   *
   * The vendor sidebar is deliberately not used for the list: the theme hides
   * it on every viewport, so a block put there would never be seen.
   *
   * The home page is not always the same component: on a forum with
   * flarum/tags the index is the tags page, on one without it the
   * discussion list. Both are extended, so the hot area is on the home page
   * either way.
   *
   * The tags page is addressed through the module registry rather than an
   * import, because flarum/tags is optional: the registry waits for the
   * module to load instead of failing when it is not there.
   */
  extend(IndexPage.prototype, 'contentItems', function (items) {
    items.add('ziiven-hot-cards', m(HotTopics, {
      variant: "cards"
    }), 200);
    items.add('ziiven-hot-bar', m(HotTopics, {
      variant: "bar"
    }), -100);
  });
  extend('ext:flarum/tags/forum/components/TagsPage', 'contentItems', function (items) {
    items.add('ziiven-hot-cards', m(HotTopics, {
      variant: "cards"
    }), 200);
    items.add('ziiven-hot-bar', m(HotTopics, {
      variant: "bar"
    }), -100);
  });

  /**
   * The column beside the posts, in Flarum's own right hand slot.
   *
   * DiscussionPage.sidebar() returns the native nav — the floor scrubber and
   * the "log in to reply" button — and the vendor page puts that nav in
   * `.Page-sidebar`. The theme hides that slot on every viewport except the
   * discussion page on a wide screen, so the block below shows up from
   * 1600px up and is hidden on the phone layout.
   *
   * The nav's own list is a DIRECT child of the nav, and the vendor CSS
   * styles it with a child selector (`.DiscussionPage-nav > ul`), so the new
   * block is appended as a SIBLING of that list rather than wrapping it: a
   * wrapper would break the selector and the list would come back with its
   * browser default padding and bullets.
   *
   * The topic page keeps its own two column grid (`.TopicLayout`), because
   * the request was about the discussion page.
   */
  extend(DiscussionPage.prototype, 'sidebar', function (sidebar) {
    if (!sidebar || !sidebar.attrs) return;
    const children = Array.isArray(sidebar.children) ? sidebar.children : [sidebar.children];
    sidebar.children = children.concat([m("div", {
      className: "DiscussionPage-sideSlot"
    }, m(TopicAside, null))]);
  });
});